/// <reference types="vite/client" />
/**
 * Structured application logger — the single logging entry point for the app.
 *
 * Replaces ad-hoc `console.*` calls. Every log is handed to a pluggable **sink**
 * (registered once at app start via {@link setLogSink}) and — in dev — mirrored
 * to the browser console so local DX is unchanged.
 *
 * The sink is injected rather than imported so this module stays a dependency-
 * free leaf: it pulls in no analytics SDK and touches no `window`, so it's safe
 * to import from workers, node-env tests, and pre-boot bootstrap code. The app
 * wires PostHog's logs API as the sink in `src/routes/__root.tsx`.
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.info('[App] Frontend started');
 *   logger.warn('[Upload] retrying', { attempt, fileName });
 *   logger.error('[Upload] failed', error);              // Error → error.* attrs
 *   logger.error('[Upload] failed', { error, fileName }); // or mixed attributes
 *
 * Levels: debug < info < warn < error. Records below the configured minimum
 * level (VITE_LOG_LEVEL; default `debug` in dev, `info` in prod) are dropped
 * before any work is done, so hot-path `logger.debug(...)` calls are cheap in
 * production and never reach the sink.
 */

// Level keys are deliberately the exact `console` method names, so the dev
// mirror can dispatch via `console[level]` with no separate mapping.
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Attribute values a sink can rely on: primitives (objects are stringified). */
export type LogAttributeValue = string | number | boolean | string[];
export type LogAttributes = Record<string, LogAttributeValue>;

/** A normalized log record handed to the sink. */
export interface LogRecord {
  level: LogLevel;
  message: string;
  attributes: LogAttributes;
}

/** A transport that ships a record somewhere (PostHog, a test spy, etc.). */
export type LogSink = (record: LogRecord) => void;

const LEVEL_RANK: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function resolveMinRank(): number {
  const configured = import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined;
  if (configured && configured in LEVEL_RANK) return LEVEL_RANK[configured];
  return import.meta.env.DEV ? LEVEL_RANK.debug : LEVEL_RANK.info;
}

const MIN_RANK = resolveMinRank();
const MIRROR_TO_CONSOLE = import.meta.env.DEV;

/**
 * Opt-in debug mode: mirror to the console and accept every level, whatever
 * the build says.
 *
 * A built preview is the one place a viewer or pipeline question can actually
 * be asked — the geometry that misbehaves is the customer's, not on anyone's
 * dev machine — and there `MIN_RANK` is `info` and the console mirror is off,
 * so `logger.debug` reaches neither the console nor a human. The alternative
 * people reach for is a stray `console.*`, which this module exists to
 * replace.
 *
 * Injected rather than read from `window` here, the same way the sink is: this
 * module stays a dependency-free leaf that touches no browser global, so it
 * remains safe to import from workers, node-env tests and pre-boot code. The
 * app edge decides (see `main.tsx`, on `?debugViewer`).
 */
let debugConsole = false;

/** Turn the console mirror (and `debug`-level records) on or off. */
export function setDebugConsole(enabled: boolean): void {
  debugConsole = enabled;
}

let sink: LogSink | null = null;

/**
 * Register the transport that receives every log record (above the min level).
 * Called once at app start. Passing `null` detaches the current sink (used on
 * teardown / in tests). Replaces any previously registered sink.
 */
export function setLogSink(next: LogSink | null): void {
  sink = next;
}

/** Attribute values pass through if primitive; everything else is stringified. */
function toAttributeValue(value: unknown): LogAttributeValue {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  if (Array.isArray(value)) return value.map((v) => String(v));
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Expand an Error into flat, queryable `error.*` (or `<prefix>.*`) attributes. */
function serializeError(err: Error, prefix = 'error'): LogAttributes {
  const attrs: LogAttributes = {
    [`${prefix}.name`]: err.name,
    [`${prefix}.message`]: err.message,
  };
  if (err.stack) attrs[`${prefix}.stack`] = err.stack;
  return attrs;
}

/**
 * The second argument to a log call is either a bag of attributes or a raw
 * error/value. Normalize both into flat attributes, unwrapping any Error
 * (top-level or nested under a key) into `*.name` / `*.message` / `*.stack`.
 */
function normalizeAttributes(input?: LogAttributes | unknown): LogAttributes {
  if (input == null) return {};
  if (input instanceof Error) return serializeError(input);
  if (typeof input !== 'object') return { detail: String(input) };

  const out: LogAttributes = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (value instanceof Error) {
      Object.assign(out, serializeError(value, key));
    } else if (value !== undefined) {
      out[key] = toAttributeValue(value);
    }
  }
  return out;
}

function log(
  level: LogLevel,
  message: string,
  attributes?: LogAttributes | unknown,
): void {
  if (LEVEL_RANK[level] < MIN_RANK && !debugConsole) return;

  const attrs = normalizeAttributes(attributes);

  if (sink) {
    // A sink failure (SDK not ready, network, etc.) must never break the app.
    try {
      sink({ level, message, attributes: attrs });
    } catch {
      // Swallow — logging is best-effort telemetry.
    }
  }

  if (MIRROR_TO_CONSOLE || debugConsole) {
    // Call `console` dynamically (not a captured reference) so test spies on
    // `console.*` still intercept the mirror. Preserve the original
    // (message, extra) shape devs are used to seeing.
    if (attributes === undefined) console[level](message);
    else console[level](message, attributes);
  }
}

/**
 * Application logger. Prefer this over `console.*` everywhere — it fans out to
 * the registered sink (PostHog logs) and mirrors to the console in development.
 */
export const logger = {
  debug: (message: string, attributes?: LogAttributes | unknown) =>
    log('debug', message, attributes),
  info: (message: string, attributes?: LogAttributes | unknown) =>
    log('info', message, attributes),
  warn: (message: string, attributes?: LogAttributes | unknown) =>
    log('warn', message, attributes),
  error: (message: string, attributes?: LogAttributes | unknown) =>
    log('error', message, attributes),
};
